import { useTranslation } from 'react-i18next';
// Utility: controlla se il groupId è valido (non default, non vuoto, pattern accettato, non UUID automatico)
function isValidGroupId(groupId) {
    if (/^aitf_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(groupId)) return false;
    // almeno 4 caratteri, solo lettere/numeri/underscore
    return true;
}
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap, Marker, Circle } from "react-leaflet";
import { getFieldLabel } from '../utils/labels.js';
import { getMinSeverity } from '../utils/dataUtils.js';
import AIMarkers from './AIMarkers';
import L from 'leaflet';
import AlertLayer, { useAlertFilters, ALERT_TYPES, SEVERITY_LEVELS } from './AlertLayer';
import AvalancheLayer from './AvalancheLayer';
import '../styles/MapResponsive.css';
import RouteNavigationModal from './RouteNavigationModal';
import POIMarkers from './POI/POIMarkers.jsx';
import OSMInfoDialog from './POI/OSMInfoDialog.jsx';
import POIEnrichmentForm from './POI/POIEnrichmentForm.jsx';
import TrailBuddyForm from './TrailBuddyForm';
import EventActivityForm from './EventActivityForm';
import EventsCalendar from './EventsCalendar';
import { useEvents } from '../hooks/useEvents';
import EventMarkers from './EventMarkers';
import { useSearchParams } from 'react-router-dom';
import EventDetailModal from './EventDetailModal';
import { getOrCreateClientId } from '../utils/clientId';
import LikeMarkers from './LikeMarkers';
import {
    normalizeText,
    isPoiFeature,
    extractPointFromFeature,
    getComuneProvinciaFromProperties,
    findComuneByPoint,
    mapPoiType,
    applyPoiFilters,
    filterPoiByMapBounds
} from '../utils/poiUtils.js';


function POIDeepLinkHandler() {
    const map = useMap();
    const [searchParams] = useSearchParams();
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        const poiParam = searchParams.get('poi');
        if (!poiParam) return;
        const parts = poiParam.split(',');
        if (parts.length < 2) return;
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (isNaN(lat) || isNaN(lng)) return;
        const zoom = parseInt(searchParams.get('zoom') || '16', 10);
        const name = searchParams.get('name') || 'Punto di interesse';
        const isEvent = searchParams.get('event') === '1';
        hasProcessed.current = true;
        const timer = setTimeout(() => {
            map.flyTo([lat, lng], zoom, { duration: 1.5 });
            map.once('moveend', () => {
                const targetLatLng = L.latLng(lat, lng);
                let closestMarker = null;
                let closestEventMarker = null;
                let minDistance = Infinity;
                let minEventDistance = Infinity;
                map.eachLayer((layer) => {
                    if (typeof layer.getLatLng === 'function') {
                        const d = targetLatLng.distanceTo(layer.getLatLng());
                        if (d < 50 && layer instanceof L.CircleMarker && !(layer instanceof L.Circle)) {
                            if (d < minEventDistance) { minEventDistance = d; closestEventMarker = layer; }
                        }
                        if (d < 50 && d < minDistance) { minDistance = d; closestMarker = layer; }
                    }
                });
                const targetMarker = isEvent && closestEventMarker ? closestEventMarker : closestMarker;
                if (targetMarker && typeof targetMarker.openPopup === 'function') {
                    targetMarker.openPopup();
                } else {
                    L.popup({ closeOnClick: true })
                        .setLatLng([lat, lng])
                        .setContent(`<div style="text-align:center; padding:8px;"><strong>${name}</strong><br/><small style="color:#666">${lat.toFixed(5)}, ${lng.toFixed(5)}</small></div>`)
                        .openOn(map);
                }
            });
        }, 1500);
        return () => clearTimeout(timer);
    }, [map, searchParams]);
    return null;
}

// Componente per catturare e aggiornare i bounds della mappa
function MapBoundsListener({ onBoundsChange }) {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const updateBounds = () => {
            const bounds = map.getBounds();
            onBoundsChange(bounds);
        };

        updateBounds();

        map.on('moveend', updateBounds);
        map.on('zoomend', updateBounds);

        return () => {
            map.off('moveend', updateBounds);
            map.off('zoomend', updateBounds);
        };
    }, [map, onBoundsChange]);

    return null;
}

const API_BASE_URL = process.env.REACT_APP_API_URL_NODE;

const TAG_TRANSLATIONS = {
    'name': 'Nome', 'alt_name': 'Nome alternativo', 'official_name': 'Nome ufficiale',
    'ref': 'Riferimento', 'amenity': 'Tipo struttura', 'tourism': 'Tipo turistico',
    'leisure': 'Tipo ricreativo', 'sport': 'Sport', 'natural': 'Elemento naturale',
    'waterway': 'Corso d\'acqua', 'alpine_hut': 'Rifugio alpino',
    'wilderness_hut': 'Rifugio non custodito', 'shelter': 'Ricovero',
    'ele': 'Altitudine (m)', 'capacity': 'Capacità (posti)', 'beds': 'Posti letto',
    'phone': 'Telefono', 'email': 'Email', 'website': 'Sito web',
    'contact:phone': 'Telefono', 'contact:mobile': 'Cellulare',
    'contact:email': 'Email', 'contact:website': 'Sito web',
    'opening_hours': 'Orari apertura', 'opening_hours:signed': 'Orari indicati',
    'opening_date': 'Data apertura', 'closing_date': 'Data chiusura',
    'operator': 'Gestore', 'operator:type': 'Tipo gestore',
    'shower': 'Doccia', 'drinking_water': 'Acqua potabile', 'toilets': 'Bagni',
    'internet_access': 'Accesso Internet', 'wheelchair': 'Accessibile disabili',
    'fee': 'A pagamento', 'payment:cash': 'Accetta contanti', 'payment:cards': 'Accetta carte',
    'addr:street': 'Via', 'addr:housenumber': 'Numero civico', 'addr:postcode': 'CAP',
    'addr:city': 'Città', 'addr:province': 'Provincia', 'addr:region': 'Regione',
    'description': 'Descrizione', 'note': 'Note', 'bridge': 'Ponte',
    'bridge:structure': 'Tipo struttura ponte', 'bridge_type': 'Tipo ponte',
    'highway': 'Tipo percorso', 'surface': 'Superficie', 'sac_scale': 'Scala SAC',
    'trail_visibility': 'Visibilità sentiero', 'width': 'Larghezza',
    '@id': 'ID OSM', '@type': 'Tipo geometria', '@timestamp': 'Ultimo aggiornamento',
    '@version': 'Versione', '@changeset': 'Changeset', '@user': 'Ultimo editore'
};

const translateTag = (key) => TAG_TRANSLATIONS[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ').replace(/:/g, ' - ');

const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Sì' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'string' && value.match(/^https?:\/\//)) return `<a href="${value}" target="_blank" rel="noopener noreferrer" style="color: #2196F3; text-decoration: none;">${value}</a>`;
    if (typeof value === 'string' && value.match(/^\+?\d[\d\s\-()]+$/)) return `<a href="tel:${value}" style="color: #2196F3; text-decoration: none;">${value}</a>`;
    if (typeof value === 'string' && value.includes('@')) return `<a href="mailto:${value}" style="color: #2196F3; text-decoration: none;">${value}</a>`;
    return value;
};

const EMERGENCY_CATEGORIES = ['wilderness_hut', 'bivouac', 'shelter'];
const isAlertPOI = (p) => { const cat = classifyPOI(p); return EMERGENCY_CATEGORIES.includes(cat) && Number(p.ele) >= 2000; };
const hasGoodDescription = (p) => typeof p.description === 'string' && p.description.length >= 120;
const hasContacts = (p) => Boolean(p.phone || p.website || p.email || p['contact:phone'] || p['contact:website']);
const isCommercialPOI = (p) => hasGoodDescription(p) && hasContacts(p);
const getPOIVisualType = (p) => { if (isAlertPOI(p)) return 'alert'; if (isCommercialPOI(p)) return 'commercial'; return 'info'; };

const classifyPOI = (p = {}) => {
    if (p.tourism === 'alpine_hut') return 'alpine_hut';
    if (p.tourism === 'wilderness_hut') return 'bivouac';
    if (p.amenity === 'shelter' || p.shelter === 'yes') return 'shelter';
    if (p.bridge === 'yes' && (p['bridge:structure'] === 'suspension' || p.bridge_type === 'suspension')) return 'suspension_bridge';
    if (p.leisure === 'adventure_park') return 'adventure_park';
    if (p.sport === 'archery') return 'sport_archery';
    if (p.leisure === 'horse_riding' || p.sport === 'equestrian') return 'sport_horse';
    if (p.sport === 'skiing' || p.piste === 'yes') return 'sport_ski';
    if (p.sport === 'paragliding' || p.aerialway === 'zip_line') return 'sport_paragliding';
    if (p.sport === 'climbing') return 'sport_climbing';
    if (p.natural === 'peak') return 'peak';
    if (p.natural === 'water' && p.water === 'lake') return 'lake';
    if (p.waterway === 'waterfall') return 'waterfall';
    if (p.natural === 'spring') return 'spring';
    if (p.natural === 'cave_entrance') return 'cave';
    if (p.amenity === 'parking') return 'parking';
    if (p.tourism === 'viewpoint') return 'viewpoint';
    if (p.amenity === 'place_of_worship') return 'church';
    if (p.historic === 'ruins') return 'ruins';
    return 'generic';
};

function ResizeMap({ filterLeftWidth }) {
    const map = useMap();
    useEffect(() => { map.invalidateSize(); }, [filterLeftWidth, map]);
    return null;
}

function FitBounds({ feature, followUser }) {
    const map = useMap();
    useEffect(() => {
        if (feature && !followUser && map) {
            try {
                const bounds = L.geoJSON(feature).getBounds();
                if (bounds.isValid()) map.fitBounds(bounds, { padding: [50, 50] });
            } catch (e) { console.warn('⚠️ Errore FitBounds:', e); }
        }
    }, [feature, followUser, map]);
    return null;
}

function FitRegionBounds({ features }) {
    const map = useMap();
    useEffect(() => {
        if (!features || features.length === 0 || !map) return;
        const timer = setTimeout(() => {
            try {
                const layer = L.geoJSON({ type: "FeatureCollection", features });
                const bounds = layer.getBounds();
                if (bounds.isValid()) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13, animate: false });
            } catch (e) { console.warn('⚠️ Impossibile calcolare bounds:', e); }
        }, 100);
        return () => clearTimeout(timer);
    }, [features, map]);
    return null;
}

function SetRegionMaxBounds({ features, onBoundsReady }) {
    const map = useMap();
    useEffect(() => {
        if (!features || features.length === 0 || !map) return;
        try {
            const layer = L.geoJSON({ type: "FeatureCollection", features });
            const bounds = layer.getBounds();
            if (bounds.isValid()) {
                const padded = bounds.pad(0.3);
                map.setMaxBounds(padded);
                const span = Math.max(
                    bounds.getNorth() - bounds.getSouth(),
                    bounds.getEast() - bounds.getWest()
                );
                map.setMinZoom(span > 6 ? 6 : span > 3 ? 7 : 8);
                if (onBoundsReady) onBoundsReady([
                    [padded.getSouth(), padded.getWest()],
                    [padded.getNorth(), padded.getEast()]
                ]);
            }
        } catch (e) { /* bounds non validi, nessun limite */ }
    }, [features, map]);
    return null;
}

export default function MapWithResizableFilters({
    nameRegion, regionData, regionDataActivity, onTrailSelect, mode = 'desktop',
    onWeatherClick, summaries = null, disableFilters = false, onMapReady, contentType,
    filteredFeatures, mappedFeatures, dynamicFilters, selectedDynamic, filtersHash,
    toggleDynamicFilter, resetFilters, getDifficultyLabel,
    groupId = null, isPersonal = false,
    focusPoiId = undefined,
    onTrailPopupClose = undefined
}) {
    const { t } = useTranslation();
    const [filterLeftWidth, setFilterLeftWidth] = useState(300);
    const isDraggingLeft = useRef(false);
    const mapRef = useRef();
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [routeModalOpen, setRouteModalOpen] = useState(false);
    const [tileBounds, setTileBounds] = useState(null);
    const [followUser, setFollowUser] = useState(true);
    const [userPosition, setUserPosition] = useState(null);
    const [downloadingMap, setDownloadingMap] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const ghostLinesRef = useRef(new Map());
    const eventMarkersRef = useRef(new Map());
    const alertControls = useAlertFilters();

    const [osmInfoDialogOpen, setOsmInfoDialogOpen] = useState(false);
    const [enrichmentFormOpen, setEnrichmentFormOpen] = useState(false);
    const [selectedPOI, setSelectedPOI] = useState(null);

    const [showTrailBuddyForm, setShowTrailBuddyForm] = useState(false);
    const [showActivityForm, setShowActivityForm] = useState(false);
    const [selectedTrailForEvent, setSelectedTrailForEvent] = useState(null);
    const [selectedPoiForEvent, setSelectedPoiForEvent] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarDefaultTab, setCalendarDefaultTab] = useState(null);
    const [eventDetailData, setEventDetailData] = useState(null);

    const { events, loading: loadingEvents, hasEvents, getEventsForTrail, loadEvents } = useEvents(nameRegion);
    const [likedTrailIds, setLikedTrailIds] = useState(new Set());

    // ─── POI Filtering State ─────────────────────────────────────────────────
    const [poiFilters, setPoiFilters] = useState({
        name: "",
        comune: "",
        provincia: "",
        tipologie: []
    });
    const [showFiltersMobilePanel, setShowFiltersMobilePanel] = useState(false);
    const [istatComuni, setIstatComuni] = useState(null);
    const [isLoadingIstat, setIsLoadingIstat] = useState(false);
    const geoLookupCacheRef = useRef(new Map());
    const [mapBounds, setMapBounds] = useState(null);
    const [debouncedPoiFilters, setDebouncedPoiFilters] = useState(poiFilters);
    const debounceTimerRef = useRef(null);

    useEffect(() => {
        if (!groupId || disableFilters) return;
        const API = process.env.REACT_APP_API_URL_NODE || '';

        fetch(`${API}/api/trails/likes/summary?groupId=${encodeURIComponent(groupId)}&region=${encodeURIComponent(nameRegion.toLowerCase())}`)
            .then(r => r.json())
            .then(data => {
                const ids = new Set((data.items || []).map(i => i.trailId));
                setLikedTrailIds(ids);
                console.log(`❤️ Liked trails: ${ids.size}`);
            })
            .catch(() => { });
    }, [groupId, nameRegion, disableFilters]);

    useEffect(() => {
        if (!disableFilters || !nameRegion) {
            setIstatComuni(null);
            return;
        }

        setIsLoadingIstat(true);
        const regionName = nameRegion.toLowerCase().replace(/\s+/g, '_');
        const istatPath = `/data/istat/${regionName}-comuni.json`;

        fetch(istatPath)
            .then(r => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then(data => {
                setIstatComuni(Array.isArray(data) ? data : data.items || []);
                console.log(`✅ ISTAT ${regionName} caricato:`, data);
                geoLookupCacheRef.current.clear();
            })
            .catch(err => {
                console.warn(`⚠️ Errore caricamento ISTAT ${regionName}:`, err.message);
                setIstatComuni(null);
            })
            .finally(() => setIsLoadingIstat(false));
    }, [nameRegion, disableFilters]);

    useEffect(() => { console.log('🔄 selectedRoute cambiato:', selectedRoute?.properties?.name || selectedRoute?.id || 'NULL'); }, [selectedRoute]);
    useEffect(() => { console.log('🔄 routeModalOpen cambiato:', routeModalOpen); }, [routeModalOpen]);
    useEffect(() => { if (mapRef.current && onMapReady) onMapReady(mapRef.current); }, [onMapReady]);

    const handleTrailSelect = (feature) => { if (!feature) return; if (onTrailSelect) onTrailSelect(feature); };

    const handleShowOSMInfo = React.useCallback((poi) => { setSelectedPOI(poi); setOsmInfoDialogOpen(true); }, []);
    const handleEnrichPOI = React.useCallback((poi) => {
        if (!disableFilters) return;
        setSelectedPOI(poi);
        setEnrichmentFormOpen(true);
    }, [disableFilters]);
    const handleCloseOSMInfo = React.useCallback(() => { setOsmInfoDialogOpen(false); }, []);
    const handleCloseEnrichmentForm = React.useCallback(() => { setEnrichmentFormOpen(false); setSelectedPOI(null); }, []);

    const resetPoiFilters = () => {
        setPoiFilters({
            name: "",
            comune: "",
            provincia: "",
            tipologie: []
        });
    };

    const preCacheMapArea = () => {
        if (!selectedRoute || !navigator.serviceWorker.controller) { alert('Service Worker non disponibile'); return; }
        setDownloadingMap(true);
        const geojsonLayer = L.geoJSON(selectedRoute);
        const bounds = geojsonLayer.getBounds();
        const handleMessage = (event) => {
            if (event.data.type === 'CACHE_COMPLETE') { setDownloadingMap(false); alert('✅ Mappa scaricata offline!'); navigator.serviceWorker.removeEventListener('message', handleMessage); }
        };
        navigator.serviceWorker.addEventListener('message', handleMessage);
        navigator.serviceWorker.controller.postMessage({ type: 'CACHE_AREA', bounds: { north: bounds.getNorth(), south: bounds.getSouth(), east: bounds.getEast(), west: bounds.getWest() }, zoom: [13, 14, 15] });
        setTimeout(() => { setDownloadingMap(false); navigator.serviceWorker.removeEventListener('message', handleMessage); }, 60000);
    };

    const shouldExcludePOI = (feature) => {
        const EXCLUDED_SHELTER_TYPES = new Set(['public_transport', 'picnic_shelter', 'sun_shelter', 'field_shelter', 'bus_stop', 'tram_stop', 'gazebo', 'shopping_cart']);
        const EXCLUDED_SHELTER_ON_PLAINS = new Set(['lean_to', 'pavilion', 'roof']);
        if (feature.geometry?.type !== 'Point') return false;
        const props = feature.properties || {};
        const category = classifyPOI(props);
        if (category === 'shelter') return EXCLUDED_SHELTER_TYPES.has(props.shelter_type);
        const elevation = Number(props.ele);
        if (!Number.isNaN(elevation) && elevation < 400 && (EXCLUDED_SHELTER_ON_PLAINS.has(props.shelter_type) || !props.shelter_type)) return true;
        return false;
    };

    useEffect(() => { if (!disableFilters) { setOsmInfoDialogOpen(false); setEnrichmentFormOpen(false); setSelectedPOI(null); } }, [disableFilters]);

    useEffect(() => {
        setOsmInfoDialogOpen(false); setEnrichmentFormOpen(false); setShowTrailBuddyForm(false);
        setShowActivityForm(false); setShowCalendar(false);
        setSelectedPOI(null); setSelectedRoute(null); setRouteModalOpen(false);
        setEventDetailData(null);
    }, [nameRegion]);

    useEffect(() => {
        const delay = mode === 'mobile' ? 500 : 0;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            setDebouncedPoiFilters(poiFilters);
        }, delay);
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [poiFilters, mode]);


    const activityPOIs = useMemo(() => {
        if (!regionDataActivity || !regionDataActivity.features) return [];
        return regionDataActivity.features.filter(f => f.geometry?.type === 'Point').filter(f => !shouldExcludePOI(f)).map(feature => {
            const props = feature.properties || {};
            return { ...feature, properties: { ...props, poiCategory: classifyPOI(props), poiVisualType: getPOIVisualType(props) } };
        });
    }, [regionDataActivity]);

    const enrichedActivityFeatures = useMemo(() => {
        if (!disableFilters) return regionData?.features || [];

        return (regionData?.features || [])
            .filter(f => !shouldExcludePOI(f))
            .map(feature => {
                if (feature.geometry?.type !== 'Point') return feature;

                const props = feature.properties || {};
                const coords = extractPointFromFeature(feature);

                const { comune: osmComune, provincia: osmProvincia, siglaProvincia: osmSiglaProvincia } = getComuneProvinciaFromProperties(feature);

                let comuneFinale = osmComune;
                let provinciaFinale = osmProvincia;
                let siglaProvinciaFinale = osmSiglaProvincia;
                let geoSource = osmComune ? 'osm' : 'unknown';
                if (!osmComune || !osmProvincia) {
                    if (coords && istatComuni) {
                        const istatResult = findComuneByPoint(coords[0], coords[1], istatComuni, geoLookupCacheRef.current);
                        if (istatResult) {
                            if (!osmComune) {
                                comuneFinale = istatResult.comune;
                                geoSource = 'istat';
                            }
                            if (!osmProvincia) {
                                provinciaFinale = istatResult.provincia;
                                siglaProvinciaFinale = istatResult.siglaProvincia;
                                if (osmComune) geoSource = 'istat';
                            }
                        }
                    }
                }
                const tipologiaFinale = mapPoiType(feature);

                const normalizedName = normalizeText(props.name || '');
                const normalizedComune = normalizeText(comuneFinale);
                const normalizedProvincia = normalizeText(provinciaFinale);

                return {
                    ...feature,
                    properties: {
                        ...props,
                        poiCategory: classifyPOI(props),
                        poiVisualType: getPOIVisualType(props),
                        tipologiaFinale,
                        comuneFinale,
                        provinciaFinale,
                        siglaProvinciaFinale,
                        normalizedName,
                        normalizedComune,
                        normalizedProvincia,
                        geoSource
                    }
                };
            });
    }, [disableFilters, regionData, istatComuni]);

    const geoJsonFeatures = disableFilters ? enrichedActivityFeatures : (mappedFeatures || []);
    const effectiveFilteredFeatures = disableFilters ? (regionData?.features || []) : (filteredFeatures || []);

    const poiFeatures = useMemo(() => {
        return geoJsonFeatures.filter(f => f.geometry?.type === 'Point').map(poi => ({ poi, distance: 0 }));
    }, [geoJsonFeatures, disableFilters]);

    const lineFeatures = useMemo(() => {
        return geoJsonFeatures.filter(f => f.geometry?.type === 'LineString' || f.geometry?.type === 'MultiLineString');
    }, [geoJsonFeatures]);

    const filteredPoiFeatures = useMemo(() => {
        const poiFeatures = enrichedActivityFeatures.filter(isPoiFeature);
        const filtersToUse = mode === 'mobile' ? debouncedPoiFilters : poiFilters;
        return applyPoiFilters(poiFeatures, filtersToUse);
    }, [
        enrichedActivityFeatures,
        mode,
        debouncedPoiFilters.name,
        debouncedPoiFilters.comune,
        debouncedPoiFilters.provincia,
        JSON.stringify(debouncedPoiFilters.tipologie),
    ]);

    const displayedPoiFeatures = useMemo(() => {
        if (mode === 'mobile' && mapBounds) {
            const visible = filterPoiByMapBounds(filteredPoiFeatures, mapBounds, 50);
            return visible.length > 0 ? visible : filteredPoiFeatures.slice(0, 50);
        }
        return filteredPoiFeatures;
    }, [filteredPoiFeatures, mapBounds, mode]);

    const poiMarkersData = useMemo(() => {
        return displayedPoiFeatures.map(poi => ({ poi, distance: 0 }));
    }, [displayedPoiFeatures]);

    const poiDynamicFilters = useMemo(() => {
        const poiFeatures = enrichedActivityFeatures.filter(isPoiFeature);

        const province = new Set();
        const tipologie = new Set();

        poiFeatures.forEach(f => {
            const props = f.properties || {};
            if (props.provinciaFinale) province.add(props.provinciaFinale);
            if (props.tipologiaFinale) tipologie.add(props.tipologiaFinale);
        });

        return {
            province: Array.from(province).sort(),
            tipologie: Array.from(tipologie).sort()
        };
    }, [enrichedActivityFeatures]);

    useEffect(() => { setIsTransitioning(true); const t = setTimeout(() => setIsTransitioning(false), 300); return () => clearTimeout(t); }, [disableFilters]);

    const handleMouseDownLeft = (e) => { e.preventDefault(); isDraggingLeft.current = true; };

    useEffect(() => {
        const handleMouseMove = (e) => { if (isDraggingLeft.current) { const w = e.clientX; if (w > 200 && w < 600) setFilterLeftWidth(w); } };
        const handleMouseUp = () => { isDraggingLeft.current = false; };
        if (isDraggingLeft.current) { document.addEventListener('mousemove', handleMouseMove); document.addEventListener('mouseup', handleMouseUp); }
        return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
    }, []);

    useEffect(() => {
        const featuresToUse = disableFilters ? geoJsonFeatures : mappedFeatures;
        window.viewRouteDetail = (featureIdOrData) => {
            let feature;
            if (typeof featureIdOrData === 'string' || typeof featureIdOrData === 'number') {
                feature = featuresToUse?.find(f => f.id === featureIdOrData || f.properties?.id === featureIdOrData);
                if (!feature) { console.error('❌ Feature NON trovata'); return; }
            } else if (typeof featureIdOrData === 'object' && featureIdOrData !== null) { feature = featureIdOrData; }
            if (feature) {
                setOsmInfoDialogOpen(false); setEnrichmentFormOpen(false); setShowTrailBuddyForm(false);
                setShowActivityForm(false); setShowCalendar(false); setSelectedPOI(null);
                setSelectedRoute(feature); setRouteModalOpen(true); setFollowUser(false);
            }
        };
        return () => { delete window.viewRouteDetail; };
    }, [geoJsonFeatures, mappedFeatures, disableFilters]);

    useEffect(() => {
        const featuresToUse = disableFilters ? geoJsonFeatures : mappedFeatures;
        window.selectTrailForWeather = (featureIdOrData) => {
            let feature;
            if (typeof featureIdOrData === 'string' || typeof featureIdOrData === 'number') feature = featuresToUse?.find(f => f.id === featureIdOrData);
            else if (typeof featureIdOrData === 'object' && featureIdOrData !== null) feature = featureIdOrData;
            if (feature) handleTrailSelect(feature);
        };
        return () => { delete window.selectTrailForWeather; };
    }, [geoJsonFeatures, mappedFeatures, onTrailSelect, disableFilters]);

    useEffect(() => {
        const featuresToUse = disableFilters ? geoJsonFeatures : mappedFeatures;
        window.openWeatherById = (featureId, fallbackLat, fallbackLon, fallbackName) => {
            const feature = featuresToUse?.find(f => f.id === featureId || f.properties?.id === featureId);
            if (feature) {
                if (onWeatherClick) onWeatherClick(feature);
                else if (onTrailSelect) onTrailSelect(feature);
            } else {
                const coords = window._weatherCoords?.[featureId];
                if (coords) {
                    const syntheticFeature = {
                        id: featureId,
                        name: coords.name,
                        geometry: { type: 'Point', coordinates: [coords.lon, coords.lat] },
                        properties: { name: coords.name, id: featureId }
                    };
                    if (onWeatherClick) onWeatherClick(syntheticFeature);
                    else if (onTrailSelect) onTrailSelect(syntheticFeature);
                } else {
                    alert('Percorso non trovato');
                }
            }
        };
        return () => { delete window.openWeatherById; };
    }, [geoJsonFeatures, mappedFeatures, onWeatherClick, onTrailSelect, disableFilters]);

    useEffect(() => {
        const featuresToUse = disableFilters ? geoJsonFeatures : mappedFeatures;
        window.openTrailBuddyForm = (featureIdOrData) => {
            let feature;
            if (typeof featureIdOrData === 'string' || typeof featureIdOrData === 'number') feature = featuresToUse?.find(f => f.id === featureIdOrData || f.properties?.id === featureIdOrData);
            else if (typeof featureIdOrData === 'object' && featureIdOrData !== null) feature = featureIdOrData;
            if (feature) { setSelectedTrailForEvent(feature); setShowTrailBuddyForm(true); }
        };
        return () => { delete window.openTrailBuddyForm; };
    }, [geoJsonFeatures, mappedFeatures, disableFilters]);

    useEffect(() => {
        const featuresToUse = disableFilters ? geoJsonFeatures : mappedFeatures;
        window.openActivityForm = (featureIdOrData) => {
            let feature;
            if (typeof featureIdOrData === 'string' || typeof featureIdOrData === 'number') feature = featuresToUse?.find(f => f.id === featureIdOrData || f.properties?.id === featureIdOrData);
            else if (typeof featureIdOrData === 'object' && featureIdOrData !== null) feature = featureIdOrData;
            if (feature) {
                if (feature.geometry?.type === 'Point') { setSelectedPoiForEvent(feature); setSelectedTrailForEvent(null); }
                else { setSelectedTrailForEvent(feature); setSelectedPoiForEvent(null); }
                setShowActivityForm(true);
            }
        };
        return () => { delete window.openActivityForm; };
    }, [geoJsonFeatures, mappedFeatures, disableFilters]);

    useEffect(() => {
        window.openCalendarEvents = (defaultTab) => {
            console.log('🎯 Apertura calendario eventi, tab:', defaultTab);
            setCalendarDefaultTab(defaultTab || null);
            setShowCalendar(true);
        };
        return () => { delete window.openCalendarEvents; };
    }, []);

    useEffect(() => {
        const API = process.env.REACT_APP_API_URL_NODE || '';
        const clientId = getOrCreateClientId();

        window.toggleLike = async (trailId, region) => {
            if (!isValidGroupId(groupId)) {
                alert(t("alert_join_group"));
                return;
            }

            const lsKey = `aitf_liked_${groupId}_${trailId}`;
            const isLiked = localStorage.getItem(lsKey) === '1';
            const btnId = `like-btn-${trailId}`;
            const btn = document.getElementById(btnId);

            if (btn) { btn.disabled = true; btn.textContent = '...'; }

            if (isLiked && !isPersonal) {
                const ok = window.confirm('Vuoi rimuovere la tua preferenza per questo percorso?');
                if (!ok) {
                    if (btn) {
                        btn.disabled = false;
                        btn.textContent = isPersonal ? '❤️ Salvato · rimuovi' : '✅ Preferito · rimuovi';
                    }
                    return;
                }
            }

            const featuresToUse = disableFilters ? geoJsonFeatures : mappedFeatures;
            const trail = featuresToUse?.find(
                f => f.id === trailId || f.properties?.id === trailId ||
                    String(f.id) === String(trailId) || String(f.properties?.id) === String(trailId)
            );
            const props = trail?.properties || {};

            const getTrailName = () => {
                if (props.name && props.name.trim() && props.name !== trailId) return props.name;
                return '';
            };
            const trailName = getTrailName();
            const from = props.from || props.da || props.partenza || props.start || '';
            const start = props.to || props.a || props.arrivo || props.end || '';

            try {
                const res = await fetch(`${API}/api/trails/${encodeURIComponent(trailId)}/like`, {
                    method: isLiked ? 'DELETE' : 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ groupId, clientId, region, trailName, from, start })
                });
                const data = await res.json();

                if (data.ok) {
                    if (isLiked) {
                        localStorage.removeItem(lsKey);
                        setLikedTrailIds(prev => { const next = new Set(prev); next.delete(trailId); return next; });
                        if (btn) {
                            btn.textContent = isPersonal ? '❤️ Salva' : '👍 Like';
                            btn.style.background = 'white';
                            btn.style.borderColor = '#ccc';
                            btn.style.color = '#333';
                            btn.disabled = false;
                        }
                    } else {
                        localStorage.setItem(lsKey, '1');
                        setLikedTrailIds(prev => new Set([...prev, trailId]));
                        if (btn) {
                            btn.textContent = isPersonal ? '❤️ Salvato · rimuovi' : '✅ Preferito · rimuovi';
                            btn.style.background = '#f0fff0';
                            btn.style.borderColor = '#4CAF50';
                            btn.style.color = '#2e7d32';
                            btn.disabled = false;
                        }
                    }
                } else {
                    if (btn) { btn.disabled = false; btn.textContent = '⚠️ Errore'; }
                    setTimeout(() => {
                        if (btn) {
                            btn.textContent = isLiked
                                ? (isPersonal ? '❤️ Salvato · rimuovi' : '✅ Preferito · rimuovi')
                                : (isPersonal ? '❤️ Salva' : '👍 Like');
                        }
                    }, 2000);
                }
            } catch {
                if (btn) { btn.disabled = false; btn.textContent = '⚠️ Errore'; }
            }
        };

        return () => { delete window.toggleLike; };
    }, [groupId, isPersonal, geoJsonFeatures, mappedFeatures, disableFilters]);

    useEffect(() => {
        window.openEventDetail = (eventId) => {
            const event = window._eventRegistry?.[eventId];
            if (event) {
                console.log('🎯 Apertura dettaglio evento:', event.eventName);
                setEventDetailData(event);
            } else {
                console.error('❌ Evento non trovato nel registry:', eventId);
            }
        };
        return () => { delete window.openEventDetail; };
    }, []);

    useEffect(() => {
        window.focusTrail = (trailId) => {
            if (!mapRef.current) return;

            const featuresToUse = disableFilters ? geoJsonFeatures : mappedFeatures;
            const trail = featuresToUse?.find(
                f => f.id === trailId || f.properties?.id === trailId
            );
            if (!trail) {
                console.warn('⚠️ Trail non trovato:', trailId);
                return;
            }

            try {
                const bounds = L.geoJSON(trail).getBounds();
                if (bounds.isValid()) {
                    mapRef.current.fitBounds(bounds, { padding: [60, 60] });
                }
            } catch (e) { console.warn(e); }

            setTimeout(() => {
                const ghostLine = ghostLinesRef.current.get(trailId);
                if (ghostLine) {
                    ghostLine.openPopup();
                } else {
                    console.warn('⚠️ Ghost line non trovata per:', trailId);
                }
            }, 600);
        };

        return () => { delete window.focusTrail; };
    }, [geoJsonFeatures, mappedFeatures, disableFilters]);

    useEffect(() => {
        function normalizePoiId(id) {
            if (!id) return '';
            const str = String(id).trim();
            const match = str.match(/^(node|way|relation)[/:](\d+)$/);
            if (match) {
                return {
                    full: `${match[1]}/${match[2]}`,
                    alt: `${match[1]}:${match[2]}`,
                    num: match[2]
                };
            }
            if (/^\d+$/.test(str)) {
                return {
                    full: str,
                    alt: str,
                    num: str
                };
            }
            return {
                full: str,
                alt: str,
                num: str.replace(/^(node:|node\/|way:|way\/|relation:|relation\/)/, '')
            };
        }

        window.focusPoi = (poiIdRaw) => {
            if (!mapRef.current) return;

            const normTarget = normalizePoiId(poiIdRaw);
            const featuresToUse = disableFilters ? geoJsonFeatures : mappedFeatures;

            if (featuresToUse?.length) {
                console.log('[focusPoi] Target:', poiIdRaw, normTarget);
                console.log('[focusPoi] POI disponibili:', featuresToUse.map(f => ({
                    id: f.id,
                    pid: f.properties?.id,
                    paid: f.properties?.['@id'],
                    norm: [normalizePoiId(f.id), normalizePoiId(f.properties?.id), normalizePoiId(f.properties?.['@id'])]
                })));
            }

            const poi = featuresToUse?.find(f => {
                const ids = [f.id, f.properties?.id, f.properties?.['@id']];
                return ids.some(fid => {
                    const norm = normalizePoiId(fid);
                    return (
                        norm.full === normTarget.full ||
                        norm.alt === normTarget.full ||
                        norm.full === normTarget.alt ||
                        norm.alt === normTarget.alt ||
                        norm.num === normTarget.num
                    );
                });
            });

            if (!poi || !poi.geometry?.coordinates) {
                console.warn('⚠️ POI non trovato:', poiIdRaw, normTarget);
                return;
            }

            const [lon, lat] = poi.geometry.coordinates;
            mapRef.current.flyTo([lat, lon], 16, { duration: 1.2 });

            mapRef.current.once('moveend', () => {
                const targetLatLng = L.latLng(lat, lon);
                let closestMarker = null;
                let minDistance = Infinity;

                mapRef.current.eachLayer((layer) => {
                    if (typeof layer.getLatLng === 'function') {
                        const d = targetLatLng.distanceTo(layer.getLatLng());
                        if (d < 100 && d < minDistance) {
                            minDistance = d;
                            closestMarker = layer;
                        }
                    }
                });

                if (closestMarker && typeof closestMarker.openPopup === 'function') {
                    closestMarker.openPopup();
                } else {
                    L.popup({ closeOnClick: true })
                        .setLatLng([lat, lon])
                        .setContent(`<div style="text-align:center; padding:8px;"><strong>${poi.properties?.name || 'Punto di interesse'}</strong></div>`)
                        .openOn(mapRef.current);
                }
            });
        };

        return () => { delete window.focusPoi; };
    }, [geoJsonFeatures, mappedFeatures, disableFilters]);

    const pathStyle = (feature) => {
        const isFiltered = effectiveFilteredFeatures?.some(f => f.id === feature.id);
        const isMobileMode = mode === 'mobile';
        const trailId = feature.id || feature.properties?.id;
        const hasEvent = trailId && hasEvents(trailId);
        const isLiked = likedTrailIds.has(trailId);

        return {
            color: isLiked ? '#e91e63'
                : hasEvent ? '#FF6B6B'
                    : isFiltered ? '#1565C0'
                        : '#cccccc',
            weight: isMobileMode ? 7 : (hasEvent ? 4 : 3),
            opacity: isFiltered || isLiked ? 0.9 : 0.3,
            lineCap: 'round', lineJoin: 'round'
        };
    };

    const onEachFeature = (feature, layer) => {
        const props = feature.properties || {};
        const geometryType = feature.geometry?.type;
        if (geometryType === 'Point') return;

        if (layer instanceof L.Polyline) {
            const originalStyle = pathStyle(feature);
            const latlngs = layer.getLatLngs();
            const ghostLineWeight = mode === 'mobile' ? 25 : 15;
            const ghostLine = L.polyline(latlngs, { color: 'transparent', weight: ghostLineWeight, opacity: 0, interactive: true, className: 'ghost-line-invisible', bubblingMouseEvents: false });
            layer.setStyle({ ...originalStyle, interactive: false });

            if (mapRef.current) {
                ghostLine.addTo(mapRef.current);
                const ghostElement = ghostLine.getElement();
                if (ghostElement) { ghostElement.style.outline = 'none'; ghostElement.style.border = 'none'; ghostElement.style.webkitTapHighlightColor = 'transparent'; }
                const featureId = feature.id || `temp_${Date.now()}`;
                ghostLinesRef.current.set(featureId, ghostLine);
                layer.bringToFront();
            }

            if (mode !== 'mobile') {
                ghostLine.on('mouseover', function () {
                    const trailId = feature.id || feature.properties?.id;
                    const hasEvent = trailId && hasEvents(trailId);
                    const liked = likedTrailIds.has(trailId);
                    layer.setStyle(
                        hasEvent ? { color: '#FF6B6B', weight: 5, opacity: 1 }
                            : liked ? { color: '#e91e63', weight: 5, opacity: 1 }
                                : { color: '#FF5722', weight: 5, opacity: 1 }
                    );
                    layer.bringToFront();
                    if (mapRef.current) mapRef.current.getContainer().style.cursor = 'pointer';
                });
                ghostLine.on('mouseout', function () {
                    const trailId = feature.id || feature.properties?.id;
                    const hasEvent = trailId && hasEvents(trailId);
                    const isFiltered = effectiveFilteredFeatures?.some(f => f.id === feature.id);
                    const liked = likedTrailIds.has(trailId);
                    layer.setStyle({
                        color: liked ? '#e91e63'
                            : hasEvent ? '#FF6B6B'
                                : isFiltered ? '#1565C0'
                                    : '#cccccc',
                        weight: mode === 'mobile' ? 7 : (hasEvent ? 4 : 3),
                        opacity: isFiltered || liked ? 0.9 : 0.3,
                        lineCap: 'round', lineJoin: 'round'
                    });
                    if (mapRef.current) mapRef.current.getContainer().style.cursor = '';
                });
                ghostLine.on('click', function () { handleTrailSelect(feature); });
            }

            if (mode === 'mobile') {
                ghostLine.on('click', function () {
                    const trailId = feature.id || feature.properties?.id;
                    const hasEvent = trailId && hasEvents(trailId);
                    layer.setStyle(hasEvent ? { color: '#FF6B6B', weight: 9, opacity: 1 } : { color: '#FF5722', weight: 9, opacity: 1 });
                    layer.bringToFront();
                    handleTrailSelect(feature);
                    ghostLine.once('popupclose', () => {
                        const isFiltered = effectiveFilteredFeatures?.some(f => f.id === feature.id);
                        layer.setStyle({ color: hasEvent ? '#FF6B6B' : (isFiltered ? '#1565C0' : '#cccccc'), weight: hasEvent ? 8 : 7, opacity: hasEvent ? 1 : (isFiltered ? 0.8 : 0.3), lineCap: 'round', lineJoin: 'round' });
                    });
                });
            }

            const getTitle = () => {
                if (props.name && props.name.trim() !== '') return props.name;
                const from = props.from || props.da || props.partenza || props.start;
                const to = props.to || props.a || props.arrivo || props.end;
                if (from && to) return `${from} - ${to}`;
                return 'Percorso senza nome';
            };

            const labelTitle = getTitle();
            let description = props.description;
            ghostLine.bindTooltip(labelTitle, { permanent: false, direction: 'top', offset: [0, -10], opacity: 0.9 });

            const getCoordinates = () => {
                if (feature.geometry && feature.geometry.coordinates) {
                    const coords = feature.geometry.coordinates;
                    if (geometryType === 'LineString') return coords;
                    else if (geometryType === 'MultiLineString') return coords[0];
                }
                return [];
            };

            const coordinates = getCoordinates();
            let googleMapsStartUrl = '#';
            let showFullRouteButton = true;

            if (geometryType === 'Point') {
                const coords = feature.geometry.coordinates;
                googleMapsStartUrl = `https://www.google.com/maps/search/?api=1&query=${coords[1]},${coords[0]}`;
                showFullRouteButton = false;
            } else if (coordinates.length > 0) {
                googleMapsStartUrl = `https://www.google.com/maps/dir/?api=1&destination=${coordinates[0][1]},${coordinates[0][0]}&travelmode=driving`;
            }

            const trailId = feature.id || props.id;
            const trailEvents = trailId ? getEventsForTrail(trailId) : [];
            const featureId = feature.id || props.id || `temp_${Date.now()}`;

            let popupContent = `<div style="max-width: 300px;"><h3 style="margin: 0 0 8px 0;">${labelTitle}</h3>`;

            if (trailEvents.length > 0) {
                if (!window._eventRegistry) window._eventRegistry = {};
                trailEvents.forEach(evt => {
                    const eid = evt._id || evt.id;
                    if (eid) window._eventRegistry[eid] = evt;
                });

                popupContent += `<div style="background: #fff3cd; padding: 12px; border-radius: 6px; margin: 12px 0; border-left: 4px solid #ffc107;">
                    <strong style="color: #856404; display: block; margin-bottom: 8px;">✨ ${trailEvents.length} evento/i in programma</strong>
                    <div style="font-size: 11px; color: #999; text-align: center; margin-bottom: 4px;">👆 Clicca per i dettagli</div>`;
                trailEvents.slice(0, 2).forEach(event => {
                    const eventDate = new Date(event.eventDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
                    const eventId = event._id || event.id;
                    const eventType = event.eventType === 'activity' ? '✨' : '🥾👥';
                    popupContent += `<div style="font-size: 13px; color: #666; padding: 6px 4px; border-top: 1px solid #ffeaa7; margin-top: 4px; cursor: pointer; border-radius: 4px; transition: background 0.2s;"
                        onclick="window.openEventDetail('${eventId}');"
                        onmouseover="this.style.background='#ffeaa7';"
                        onmouseout="this.style.background='transparent';"
                        title="Clicca per vedere i dettagli"
                    >${eventType} 📅 ${eventDate} - ${event.eventName} <span style="color: #4CAF50;">›</span></div>`;
                });
                if (trailEvents.length > 2) popupContent += `<div style="text-align: center; margin-top: 8px; padding-top: 8px; border-top: 1px solid #ffeaa7; font-size: 12px; color: #666;">+${trailEvents.length - 2} altri eventi</div>`;
                popupContent += `</div>`;
            }

            if (description && description.trim() !== '' && description.trim() !== null) {
                popupContent += `<div style="margin: 8px 0; padding: 10px; background: #f9f9f9; border-radius: 6px; border-left: 3px solid #2196F3; font-size: 13px; line-height: 1.6; color: #333;">${description}</div>`;
            }

            if (geometryType !== 'Point') {
                if (props.lengthKm) popupContent += `<p><b>Lunghezza:</b> ${props.lengthKm} km</p>`;
                if (props.ascentMeters) popupContent += `<p><b>Dislivello:</b> ${props.ascentMeters} m</p>`;
                if (props.totalDurationMin) popupContent += `<p><b>Durata:</b> ${props.totalDurationMin} min</p>`;
            }

            popupContent += `<div style="margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap;">`;
            const linkText = geometryType === 'Point' ? `${t('view_on_gmaps')}` : `${t('start_route')}`;
            popupContent += `<a href="${googleMapsStartUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 8px 14px; background: #4285F4; color: white; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; cursor: pointer;">${linkText}</a>`;

            if (showFullRouteButton && !disableFilters) {
                popupContent += `<button style="padding: 8px 14px; background: #4CAF50; color: white; border: none; border-radius: 4px; font-size: 14px; font-weight: 500; cursor: pointer;" onclick="window.viewRouteDetail('${featureId}');">${t('view_route')}</button>`;
                popupContent += `<button style="padding: 8px 14px; background: #2196F3; color: white; border: none; border-radius: 4px; font-size: 14px; font-weight: 500; cursor: pointer;" onclick="window.openWeatherById('${featureId}');" title="Meteo e allerte">${t('weather')}</button>`;
                // NB: Traduzione dinamica non possibile qui, label fissa in italiano
                popupContent += `<button style="padding: 8px 14px; background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%); color: white; border: none; border-radius: 4px; font-size: 14px; font-weight: 500; cursor: pointer; box-shadow: 0 2px 4px rgba(76,175,80,0.3);" onclick="window.openTrailBuddyForm('${featureId}');" title="Cerca compagni per questo percorso">🥾👥 ${t('find_buddies_for_track')}</button>`;
            }

            if (!disableFilters) {
                let isLiked = false;
                let likeLabel = isPersonal ? '❤️ Salva' : '👍 Like';
                let likeBg = 'white';
                let likeColor = '#333';
                let likeBorder = '#ccc';
                if (groupId) {
                    const lsKey = `aitf_liked_${groupId}_${trailId}`;
                    isLiked = localStorage.getItem(lsKey) === '1';
                    likeLabel = isLiked
                        ? (isPersonal ? '❤️ Salvato · rimuovi' : '✅ Preferito · rimuovi')
                        : (isPersonal ? '❤️ Salva' : '👍 Like');
                    likeBg = isLiked ? '#f0fff0' : 'white';
                    likeColor = isLiked ? '#2e7d32' : '#333';
                    likeBorder = isLiked ? '#4CAF50' : '#ccc';
                }
                popupContent += `
                <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #eee;">
                    <button
                        id="like-btn-${trailId}"
                        onclick="window.toggleLike('${trailId}', '${nameRegion.toLowerCase()}')"
                        style="padding: 6px 14px; border-radius: 6px; cursor: pointer; border: 1px solid ${likeBorder};
                            font-size: 13px; font-weight: 600; background: ${likeBg}; color: ${likeColor};
                            min-height: 36px; transition: all 0.2s;"
                    >${likeLabel}</button>
                </div>`;
            }
            popupContent += `</div></div>`;
            ghostLine.bindPopup(popupContent);
            ghostLine.on('popupclose', () => {
                if (onTrailPopupClose) onTrailPopupClose();
            });
        }
    };

    useEffect(() => {
        return () => {
            ghostLinesRef.current.forEach((ghostLine) => { if (mapRef.current && ghostLine) mapRef.current.removeLayer(ghostLine); });
            ghostLinesRef.current.clear();
        };
    }, [filtersHash, disableFilters]);

    if (!regionData) return <div style={{ padding: '20px' }}>Caricamento dati in corso...</div>;

    const showFiltersPanel = mode !== 'mobile' || showFiltersMobilePanel;

    // ─── Resize handle condiviso ─────────────────────────────────────────────
    const ResizeHandle = () => (
        <div
            onMouseDown={handleMouseDownLeft}
            style={{ width: '5px', cursor: 'col-resize', backgroundColor: '#ddd', transition: isDraggingLeft.current ? 'none' : 'background-color 0.2s' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#999')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#ddd')}
        />
    );

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

            {/* ─── Pannello filtri Trekking ─────────────────────────────────── */}
            {showFiltersPanel && contentType === "trekkings" && (
                <>
                    <div
                        className="filter-panel-left"
                        style={{ width: `${filterLeftWidth}px`, minWidth: '200px', maxWidth: '600px', borderRight: '1px solid #ccc', overflowY: 'auto', padding: '16px', backgroundColor: '#f9f9f9' }}
                    >
                        <button
                            onClick={() => setShowCalendar(true)}
                            style={{ width: '100%', marginBottom: '16px', padding: '12px 16px', background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.3s' }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.4)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                        >
                            🥾👥 {t('find_buddies')}
                            {events.filter(e => e.eventType === 'trail_buddy').length > 0 && (
                                <span style={{ background: 'white', color: '#4CAF50', padding: '2px 8px', borderRadius: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                                    {events.filter(e => e.eventType === 'trail_buddy').length}
                                </span>
                            )}
                        </button>

                        <h2 style={{ marginTop: 0 }}>{t('filters')}</h2>
                        <button onClick={resetFilters} style={{ width: '100%', marginBottom: '16px', padding: '8px 16px', background: '#ff5722', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                            {t('reset_filters')}
                        </button>

                        {dynamicFilters && Object.entries(dynamicFilters).map(([field, values]) => (
                            <div key={field} style={{ marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '16px', marginBottom: '8px' }}>{getFieldLabel(field, t)}</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {values.map(val => {
                                        const valStr = String(val);
                                        const isSelected = Boolean(selectedDynamic[field]?.has(valStr));
                                        return (
                                            <label key={valStr} style={{ display: 'flex', alignItems: 'center', padding: '8px', background: isSelected ? '#e3f2fd' : 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                                                <input type="checkbox" checked={isSelected} onChange={() => toggleDynamicFilter(field, valStr)} style={{ marginRight: '8px' }} />
                                                {getDifficultyLabel(valStr)}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                    <ResizeHandle />
                </>
            )}

            {/* ─── Pannello filtri Attività / POI ──────────────────────────── */}
            {showFiltersPanel && disableFilters && (
                <>
                    <div
                        className="filter-panel-left"
                        style={{ width: `${filterLeftWidth}px`, minWidth: '200px', maxWidth: '600px', borderRight: '1px solid #ccc', overflowY: 'auto', padding: '16px', backgroundColor: '#f9f9f9' }}
                    >
                        <h2 style={{ marginTop: 0 }}>{t('filters')}</h2>
                        <button onClick={resetPoiFilters} style={{ width: '100%', marginBottom: '16px', padding: '8px 16px', background: '#ff5722', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                            {t('reset_filters')}
                        </button>

                        {/* Nome Attività */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>{t('activity_name')}</label>
                            <input
                                type="text"
                                placeholder={t('search_by_name')}
                                value={poiFilters.name}
                                onChange={e => setPoiFilters(prev => ({ ...prev, name: e.target.value }))}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }}
                            />
                        </div>

                        {/* Comune */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>{t('municipality')}</label>
                            <input
                                type="text"
                                placeholder={t('search_by_municipality')}
                                value={poiFilters.comune}
                                onChange={e => setPoiFilters(prev => ({ ...prev, comune: e.target.value }))}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }}
                            />
                        </div>

                        {/* Provincia */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>{t('province')}</label>
                            <select
                                value={poiFilters.provincia}
                                onChange={e => setPoiFilters(prev => ({ ...prev, provincia: e.target.value }))}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }}
                            >
                                <option value="">-- {t('all')} --</option>
                                {poiDynamicFilters.province.map(prov => (
                                    <option key={prov} value={prov}>{prov}</option>
                                ))}
                            </select>
                        </div>

                        {/* Tipologia */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>{t('type')}</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {poiDynamicFilters.tipologie.map(tipo => (
                                    <label key={tipo} style={{ display: 'flex', alignItems: 'center', padding: '6px', backgroundColor: poiFilters.tipologie.includes(tipo) ? '#e3f2fd' : 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={poiFilters.tipologie.includes(tipo)}
                                            onChange={() => setPoiFilters(prev => ({
                                                ...prev,
                                                tipologie: prev.tipologie.includes(tipo)
                                                    ? prev.tipologie.filter(t => t !== tipo)
                                                    : [...prev.tipologie, tipo]
                                            }))}
                                            style={{ marginRight: '8px' }}
                                        />
                                        <span style={{ fontSize: '13px' }}>{tipo}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <ResizeHandle />
                </>
            )}

            {/* ─── Area mappa ──────────────────────────────────────────────── */}
            <div style={{ flex: 1, position: 'relative' }}>

                {/* Bottone toggle filtri mobile */}
                {mode === 'mobile' && (
                    <button
                        onClick={() => setShowFiltersMobilePanel(!showFiltersMobilePanel)}
                        style={{ position: 'absolute', top: 12, right: 12, zIndex: 10001, padding: '8px 12px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                    >
                        {showFiltersMobilePanel ? '✕ Chiudi' : '⚙️ Filtri'}
                    </button>
                )}

                {/* Banner caricamento eventi */}
                {loadingEvents && (
                    <div style={{ position: 'absolute', bottom: 12, left: 12, zIndex: 10000, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.92)', border: '1px solid #e0e0e0', borderRadius: 8, padding: '7px 13px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: 13, color: '#555', pointerEvents: 'none', backdropFilter: 'blur(4px)' }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" style={{ animation: 'spin 0.9s linear infinite', flexShrink: 0 }}>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            <circle cx="8" cy="8" r="6" fill="none" stroke="#ddd" strokeWidth="2.5" />
                            <path d="M8 2 A6 6 0 0 1 14 8" fill="none" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                        Caricamento eventi ed info attività da database…
                    </div>
                )}

                <MapContainer
                    zoomAnimation={false}
                    markerZoomAnimation={false}
                    ref={mapRef}
                    center={[42.5, 12.5]}
                    zoom={7}
                    minZoom={6}
                    style={{ height: '100%', width: '100%' }}
                    key={`map-${disableFilters ? 'activity' : 'trekking'}`}
                >
                    <TileLayer
                        url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                        attribution="© OpenTopoMap contributors"
                        maxZoom={17}
                        updateWhenZooming={false}
                        updateWhenIdle={true}
                        keepBuffer={4}
                        bounds={tileBounds || undefined}
                    />
                    {mode === 'mobile' && <MapBoundsListener onBoundsChange={setMapBounds} />}
                    <POIDeepLinkHandler />
                    {regionData && lineFeatures.length > 0 && !isTransitioning && (
                        <GeoJSON
                            key={`geojson-lines-${disableFilters ? 'activity' : 'trekking'}-${filtersHash}-e${events.length}`}
                            data={{ type: "FeatureCollection", features: lineFeatures }}
                            style={pathStyle}
                            onEachFeature={onEachFeature}
                        />
                    )}
                    {!disableFilters && likedTrailIds.size > 0 && (
                        <LikeMarkers likedTrailIds={likedTrailIds} mappedFeatures={mappedFeatures} isPersonal={isPersonal} />
                    )}
                    {disableFilters && displayedPoiFeatures && displayedPoiFeatures.length > 0 && !isTransitioning && (
                        <POIMarkers pois={poiMarkersData} onShowOSMInfo={handleShowOSMInfo} onEnrichPOI={handleEnrichPOI} />
                    )}
                    {geoJsonFeatures && geoJsonFeatures.length > 0 && !isTransitioning && (
                        <FitRegionBounds features={geoJsonFeatures} />
                    )}
                    {geoJsonFeatures && geoJsonFeatures.length > 0 && (
                        <SetRegionMaxBounds features={geoJsonFeatures} onBoundsReady={setTileBounds} />
                    )}
                    {!disableFilters && effectiveFilteredFeatures && !isTransitioning && (
                        <AIMarkers features={effectiveFilteredFeatures} summaries={summaries} />
                    )}
                    {!disableFilters && effectiveFilteredFeatures && events && events.length > 0 && !isTransitioning && (
                        <EventMarkers features={effectiveFilteredFeatures} events={events} nameRegion={nameRegion} contentType={contentType} />
                    )}
                    {disableFilters && poiFeatures && events && events.length > 0 && poiFeatures.length > 0 && !isTransitioning && (
                        <EventMarkers features={poiFeatures} events={events} nameRegion={nameRegion} contentType={contentType} />
                    )}
                    {!disableFilters && !isTransitioning && (
                        <>
                            <AlertLayer enabled={alertControls.enabled} typeFilters={alertControls.typeFilters} severityFilters={alertControls.severityFilters} showBadge={true} />
                            <AvalancheLayer minDangerLevel={getMinSeverity(alertControls)} showTooltips={true} />
                        </>
                    )}
                    <ResizeMap filterLeftWidth={filterLeftWidth} />
                </MapContainer>

                {isTransitioning && (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', padding: '20px 40px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 2000, fontSize: '16px', fontWeight: '600', color: '#666' }}>
                        🔄 Caricamento...
                    </div>
                )}

                {/* Badge contatori */}
                {mode !== 'mobile' && !disableFilters && !isTransitioning && (
                    <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '8px 16px', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 1000, fontWeight: 'bold' }}>
                        {effectiveFilteredFeatures?.length || 0} {t('tracks_found')}
                    </div>
                )}
                {mode !== 'mobile' && disableFilters && !isTransitioning && (
                    <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '8px 16px', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 1000, fontWeight: 'bold' }}>
                        {filteredPoiFeatures?.length || 0} {t('activities_found')}
                    </div>
                )}
                {mode === 'mobile' && disableFilters && !isTransitioning && (
                    <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '8px 12px', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 1000, fontWeight: 'bold', fontSize: '12px' }}>
                        📍 {displayedPoiFeatures?.length || 0}/{filteredPoiFeatures?.length || 0}
                    </div>
                )}
            </div>

            {/* ─── Dialogs e Modal ─────────────────────────────────────────── */}
            {disableFilters && osmInfoDialogOpen && selectedPOI && (
                <OSMInfoDialog onClose={handleCloseOSMInfo} poi={selectedPOI} />
            )}
            {disableFilters && enrichmentFormOpen && selectedPOI && (
                <POIEnrichmentForm
                    key={`poi-form-${selectedPOI?.properties?.['@id'] || Date.now()}`}
                    onClose={handleCloseEnrichmentForm}
                    poi={selectedPOI}
                />
            )}
            {showTrailBuddyForm && selectedTrailForEvent && (
                <TrailBuddyForm
                    trail={selectedTrailForEvent}
                    nameRegion={nameRegion}
                    onClose={() => { setShowTrailBuddyForm(false); setSelectedTrailForEvent(null); }}
                    onSuccess={() => { loadEvents(); setShowTrailBuddyForm(false); setSelectedTrailForEvent(null); }}
                />
            )}
            {showActivityForm && selectedPoiForEvent && (
                <EventActivityForm
                    poi={selectedPoiForEvent}
                    nameRegion={nameRegion}
                    onClose={() => { setShowActivityForm(false); setSelectedTrailForEvent(null); setSelectedPoiForEvent(null); }}
                    onSuccess={() => { loadEvents(); setShowActivityForm(false); setSelectedTrailForEvent(null); setSelectedPoiForEvent(null); }}
                />
            )}
            {showCalendar && (
                <EventsCalendar
                    region={nameRegion}
                    defaultTab={calendarDefaultTab}
                    onClose={() => setShowCalendar(false)}
                />
            )}
            {eventDetailData && (
                <EventDetailModal
                    event={eventDetailData}
                    onClose={() => setEventDetailData(null)}
                    onEventUpdated={loadEvents}
                />
            )}

            {/* ─── RouteNavigationModal ─────────────────────────────────────── */}
            <RouteNavigationModal
                isOpen={routeModalOpen}
                selectedRoute={selectedRoute}
                followUser={followUser}
                setFollowUser={setFollowUser}
                downloadingMap={downloadingMap}
                allPOIs={[...geoJsonFeatures.filter(f => f.geometry?.type === 'Point'), ...activityPOIs]}
                onClose={() => {
                    setRouteModalOpen(false);
                    setFollowUser(false);
                    if (mapRef.current && geoJsonFeatures && geoJsonFeatures.length > 0) {
                        setTimeout(() => {
                            try {
                                const layer = L.geoJSON({ type: "FeatureCollection", features: geoJsonFeatures });
                                const bounds = layer.getBounds();
                                if (bounds.isValid()) mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 13, animate: true, duration: 0.5 });
                            } catch (e) { console.warn('⚠️ Errore ripristino bounds:', e); }
                        }, 100);
                    }
                }}
                onDownloadMap={preCacheMapArea}
            />
        </div>
    );
}
